// Transferencias entre bodegas. Producto a producto o precargando una compra.
// Inventario → Operaciones → Transferencias.
// Con el flag "requiere recepción" (script 392) la transferencia queda EN
// TRÁNSITO al confirmar; las pestañas Recepciones y Tránsito gestionan el ciclo.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ArrowLeftRight, Download, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  BuscadorProducto, Campo, Location, Lote, ProductoOpt, RecepcionDoc, UbicacionOpt,
  fieldCls, invRpc, invalidateInventario, locationsList, lotesByProducto, num, productosOptList,
  recepcionLineasDoc, recepcionesConfirmadas, toNum, ubicacionesList,
} from './shared';
import { DocumentoImprimible, DocColumn, DocMeta } from './DocumentoImprimible';
import { RecepcionesPendientes, TransitosAbiertos, usePendientes, TransfConfig } from './Transito';
import { fechaLocal } from '@/lib/utils/fecha';
import { useTranslation } from 'react-i18next';

type PrintDoc = { titulo: string; codigo: string; meta: DocMeta[]; columns: DocColumn[]; filas: ReactNode[][]; totalLabel?: string; totalValue?: ReactNode };

interface Linea { key: string; producto_id: string; lote_id: string; cantidad: number; ubicacion_origen_id: string; ubicacion_destino_id: string }
let seq = 0;
const nuevaLinea = (): Linea => ({ key: `t${seq++}`, producto_id: '', lote_id: '', cantidad: 1, ubicacion_origen_id: '', ubicacion_destino_id: '' });
const hoy = () => fechaLocal();

function LoteSelect({ productoId, value, onChange }: { productoId: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('inventario');
  const { data: lotes = [] } = useQuery<Lote[]>({
    queryKey: ['inv-lotes', productoId], enabled: !!productoId, queryFn: () => lotesByProducto(productoId),
  });
  if (!productoId || lotes.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <select className={`${fieldCls} h-9`} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('transf.auto_fefo')}</option>
      {lotes.map((l) => <option key={l.id} value={l.id}>{l.numero_lote}{l.fecha_caducidad ? ` ${t('transf.vence', { fecha: l.fecha_caducidad })}` : ''}</option>)}
    </select>
  );
}

export function Transferencias() {
  const { t } = useTranslation('inventario');
  const [tab, setTab] = useState<'nueva' | 'recepciones' | 'transito'>('nueva');
  const { data: pendientes = [] } = usePendientes();
  const { data: cfg } = useQuery<TransfConfig>({ queryKey: ['inv-transf-config'], queryFn: () => invRpc<TransfConfig>('inv_transf_config_get', {}) });

  const tabBtn = (k: 'nueva' | 'recepciones' | 'transito', l: string, badge?: number) => (
    <button type="button" onClick={() => setTab(k)}
      className={`relative rounded px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
      {l}
      {(badge ?? 0) > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">{badge}</span>}
    </button>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ArrowLeftRight className="w-5 h-5" /> {t('transf.titulo')}</h1>
        <div className="flex rounded-md border p-0.5">
          {tabBtn('nueva', t('transf.tab_nueva'))}
          {tabBtn('recepciones', t('transf.tab_recepciones'), pendientes.length)}
          {tabBtn('transito', t('transf.tab_transito'))}
        </div>
      </div>
      {tab === 'nueva' && <NuevaTransferencia requiereRecepcion={!!cfg?.requiere_recepcion} />}
      {tab === 'recepciones' && <RecepcionesPendientes />}
      {tab === 'transito' && <TransitosAbiertos />}
    </div>
  );
}

function NuevaTransferencia({ requiereRecepcion }: { requiereRecepcion: boolean }) {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: bodegasTodas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const bodegas = useMemo(() => bodegasTodas.filter((b) => (b as Location & { tipo?: string }).tipo !== 'transito'), [bodegasTodas]);
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const { data: recepciones = [] } = useQuery<RecepcionDoc[]>({ queryKey: ['inv-recepciones-conf'], queryFn: recepcionesConfirmadas });

  const [origenId, setOrigenId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [fecha] = useState(hoy());
  const [obs, setObs] = useState('');
  const [recepcionId, setRecepcionId] = useState('');
  const [srcRecepcion, setSrcRecepcion] = useState(''); // recepción realmente precargada (para trazabilidad)
  const [origenLocked, setOrigenLocked] = useState(false);
  const [printDoc, setPrintDoc] = useState<PrintDoc | null>(null);
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);

  const { data: ubicOrg = [] } = useQuery<UbicacionOpt[]>({ queryKey: ['inv-ubic-org', origenId], enabled: !!origenId, queryFn: () => ubicacionesList(origenId) });
  const { data: ubicDst = [] } = useQuery<UbicacionOpt[]>({ queryKey: ['inv-ubic-dst', destinoId], enabled: !!destinoId, queryFn: () => ubicacionesList(destinoId) });

  const setLinea = (key: string, patch: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const cargar = useMutation({
    mutationFn: () => recepcionLineasDoc(recepcionId),
    onSuccess: (lns) => {
      const rec = recepciones.find((r) => r.id === recepcionId);
      if (!lns.length) { toast.error(t('transf.recepcion_sin_lineas')); return; }
      if (rec) { setOrigenId(rec.location_id); setOrigenLocked(true); }
      setSrcRecepcion(recepcionId);
      setLineas(lns.map((l) => ({ key: `t${seq++}`, producto_id: l.producto_id, lote_id: l.lote_id || '', cantidad: Number(l.cantidad) || 1, ubicacion_origen_id: '', ubicacion_destino_id: '' })));
      toast.success(t('transf.precargadas', { n: lns.length, codigo: rec?.codigo ?? t('transf.la_recepcion') }));
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const confirmar = useMutation({
    mutationFn: async () => {
      if (!origenId || !destinoId) throw new Error(t('transf.err_origen_destino'));
      if (origenId === destinoId) throw new Error(t('transf.err_distintos'));
      const usables = lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0);
      if (usables.length === 0) throw new Error(t('transf.err_sin_lineas'));
      const payload = {
        location_origen_id: origenId, location_destino_id: destinoId, fecha, observaciones: obs || null,
        recepcion_id: srcRecepcion || null,
        lineas: usables.map((l) => ({ producto_id: l.producto_id, lote_id: l.lote_id || null, cantidad: Number(l.cantidad), ubicacion_origen_id: l.ubicacion_origen_id || null, ubicacion_destino_id: l.ubicacion_destino_id || null })),
      };
      return invRpc<{ transferencia_id: string; codigo: string; total_lineas: number; estado?: string }>('inv_transferencia_confirmar', { payload });
    },
    onSuccess: (r) => {
      const enTransito = (r as { estado?: string }).estado === 'enviado';
      toast.success(enTransito
        ? t('transf.enviada', { codigo: r.codigo })
        : t('transf.confirmada', { codigo: r.codigo, n: r.total_lineas }));
      const usables = lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0);
      const prodLabel = (id: string) => { const p = productos.find((x) => x.id === id); return p ? `${p.codigo} · ${p.nombre}` : id; };
      const locName = (id: string) => bodegas.find((b) => b.id === id)?.nombre ?? '—';
      setPrintDoc({
        titulo: t('transf.doc_titulo'), codigo: r.codigo,
        meta: [
          { label: t('transf.fecha'), value: fecha },
          { label: t('transf.origen'), value: locName(origenId) },
          { label: t('transf.destino'), value: locName(destinoId) },
          { label: t('transf.observaciones'), value: obs || '—' },
        ],
        columns: [{ header: t('transf.producto') }, { header: t('transf.cantidad'), right: true }],
        filas: usables.map((l) => [prodLabel(l.producto_id), num(l.cantidad, 2)]),
        totalLabel: t('transf.total_unidades'), totalValue: num(usables.reduce((s, l) => s + Number(l.cantidad || 0), 0), 2),
      });
      setLineas([nuevaLinea()]); setRecepcionId(''); setOrigenLocked(false); setObs('');
      invalidateInventario(qc);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalUnid = useMemo(() => lineas.reduce((s, l) => s + Number(l.cantidad || 0), 0), [lineas]);

  // Validación de cabecera/líneas (la RPC vuelve a validar en el servidor)
  const faltantes = useMemo(() => {
    const f: string[] = [];
    if (!origenId) f.push(t('transf.f_origen'));
    if (!destinoId) f.push(t('transf.f_destino'));
    if (origenId && destinoId && origenId === destinoId) f.push(t('transf.f_distintos'));
    if (!fecha) f.push(t('transf.f_fecha'));
    else if (fecha > hoy()) f.push(t('transf.f_fecha_futura'));
    if (!lineas.some((l) => l.producto_id && Number(l.cantidad) > 0)) f.push(t('transf.f_linea'));
    return f;
  }, [origenId, destinoId, fecha, lineas, t]);

  return (
    <div className="space-y-4">
      {dlg.ui}
      {bodegas.length < 2 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          {t('transf.necesitas_dos')} <span className="font-medium">{t('transf.ruta_bodegas')}</span>.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('transf.cabecera')}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Campo label={origenLocked ? t('transf.origen_fijado') : t('transf.origen_req')}>
            <select className={fieldCls} value={origenId} disabled={origenLocked} onChange={(e) => setOrigenId(e.target.value)}>
              <option value="">{t('transf.selecciona')}</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label={t('transf.destino_req')}>
            <select className={fieldCls} value={destinoId} onChange={(e) => setDestinoId(e.target.value)}>
              <option value="">{t('transf.selecciona')}</option>
              {bodegas.filter((b) => b.id !== origenId).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label={t('transf.fecha')}>
            <input className={`${fieldCls} bg-muted/50 cursor-not-allowed`} value={fecha} readOnly title={t('transf.fecha_hoy')} />
          </Campo>
          <Campo label={t('transf.precargar')} className="md:col-span-2">
            <div className="flex gap-2">
              <select className={fieldCls} value={recepcionId}
                onChange={(e) => { setRecepcionId(e.target.value); if (!e.target.value) setOrigenLocked(false); }}>
                <option value="">{t('transf.opcional')}</option>
                {recepciones.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.fecha}{r.referencia ? ` · ${r.referencia}` : ''}</option>)}
              </select>
              <Button variant="outline" disabled={!recepcionId} isLoading={cargar.isPending} onClick={() => cargar.mutate()}><Download className="w-4 h-4 mr-1" /> {t('transf.cargar')}</Button>
            </div>
          </Campo>
          <Campo label={t('transf.observaciones')}><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} /></Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('transf.lineas', { n: lineas.length })}</CardTitle>
          <Button variant="outline" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}><Plus className="w-4 h-4 mr-1" /> {t('transf.linea')}</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-2">{t('transf.producto')}</th><th className="py-2 pr-2 w-56">{t('transf.lote')}</th>
              <th className="py-2 pr-2 w-40">{t('transf.ubic_origen')}</th><th className="py-2 pr-2 w-40">{t('transf.ubic_destino')}</th>
              <th className="py-2 pr-2 w-28 text-right">{t('transf.cantidad')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-2 pr-2 min-w-56">
                    <BuscadorProducto productos={productos} value={l.producto_id} compact
                      onChange={(id) => setLinea(l.key, { producto_id: id, lote_id: '' })} />
                  </td>
                  <td className="py-2 pr-2"><LoteSelect productoId={l.producto_id} value={l.lote_id} onChange={(v) => setLinea(l.key, { lote_id: v })} /></td>
                  <td className="py-2 pr-2">
                    <select className={`${fieldCls} h-9`} value={l.ubicacion_origen_id} onChange={(e) => setLinea(l.key, { ubicacion_origen_id: e.target.value })}>
                      <option value="">{t('transf.general')}</option>
                      {ubicOrg.map((u) => <option key={u.id} value={u.id}>{u.codigo}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-2">
                    <select className={`${fieldCls} h-9`} value={l.ubicacion_destino_id} onChange={(e) => setLinea(l.key, { ubicacion_destino_id: e.target.value })}>
                      <option value="">{t('transf.general')}</option>
                      {ubicDst.map((u) => <option key={u.id} value={u.id}>{u.codigo}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-2"><input type="number" step="0.0001" min="0" className={`${fieldCls} h-9 text-right`} value={l.cantidad} onChange={(e) => setLinea(l.key, { cantidad: toNum(e.target.value) })} /></td>
                  <td className="py-2 text-right"><button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">{t('transf.nota_fefo')}</p>
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">{t('transf.total_unidades')}: <span className="font-semibold text-foreground">{totalUnid}</span></div>
        <div className="flex items-center gap-3">
        {faltantes.length > 0 && (
          <span className="text-xs text-amber-600 dark:text-amber-400">{t('transf.falta', { lista: faltantes.join(', ') })}</span>
        )}
        <Button isLoading={confirmar.isPending} disabled={faltantes.length > 0} onClick={async () => {
          const n = lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0).length;
          const o = bodegas.find((b) => b.id === origenId)?.nombre ?? '';
          const d = bodegas.find((b) => b.id === destinoId)?.nombre ?? '';
          const ok = requiereRecepcion
            ? await dlg.confirmar(t('transf.confirmar_enviar', { n, o, d }), { detalle: t('transf.detalle_enviar') })
            : await dlg.confirmar(t('transf.confirmar_conf', { n, o, d }), { detalle: t('transf.detalle_conf') });
          if (ok) confirmar.mutate();
        }}>{requiereRecepcion ? t('transf.btn_enviar') : t('transf.btn_confirmar')}</Button>
        </div>
      </div>

      {printDoc && <DocumentoImprimible {...printDoc} onClose={() => setPrintDoc(null)} />}
    </div>
  );
}
